import { KioskPreferencesQuestionsIds } from 'modules/selfServices/constants';

import Flow from 'types/Flow';
import { BreakdownServiceAuthorType } from 'modules/selfServices/types/SelfService';
import { SelfServiceAPISteps, SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';

import { isKioskPreferenceQuestionVisible } from 'modules/selfServices/utils';

import { BREAKDOWN_SIGNATURE } from './common';
import {
  FINAL_INSTRUCTIONS,
  PARKING as COMMON_PARKING,
  QUESTIONS as COMMON_QUESTIONS,
  KIOSK_USAGE as COMMON_KIOSK_USAGE,
  CUSTOMER_INFO as COMMON_CUSTOMER_INFO,
} from '../common';
import { isNotAnswered } from '../utils';
import BreakdownInfo from '../../../BreakdownInfo';
import type { SelfServiceStep } from '../../types';
import Extras from '../../../Extras';
import Basket from '../../../Basket';

export const CUSTOMER_INFO: SelfServiceStep = {
  ...COMMON_CUSTOMER_INFO,
  next: SelfServiceSteps.BREAKDOWN_INFO,
};

export const BREAKDOWN_INFO: SelfServiceStep = {
  key: SelfServiceSteps.BREAKDOWN_INFO,
  apiKey: SelfServiceAPISteps.ISSUE_DESCRIPTION,
  component: BreakdownInfo,
  previous: SelfServiceSteps.CUSTOMER_INFO,
  next: SelfServiceSteps.EXTRAS,
  data: {
    isVisible: ({ status }) => isNotAnswered(status),
  },
};

export const EXTRAS: SelfServiceStep = {
  key:SelfServiceSteps.EXTRAS,
  apiKey:SelfServiceAPISteps.EXTRAS_ADDITIONAL_SERVICES,
  component:Extras,
  previous: SelfServiceSteps.BREAKDOWN_INFO,
  next: SelfServiceSteps.QUESTIONS,
  data: {
    isVisible: ({ status, checklists }) =>
      isNotAnswered(status) &&
        Boolean(checklists?.some(({ items = [] }) => items.some(({ advices = [] }) => advices.length > 0)))
  },
};

export const QUESTIONS: SelfServiceStep = {
  ...COMMON_QUESTIONS,
  previous: EXTRAS.key,
  next: SelfServiceSteps.KIOSK_USAGE,
};

export const BASKET: SelfServiceStep = {
  key: SelfServiceSteps.BASKET,
  apiKey: SelfServiceAPISteps.BASKET,
  component: Basket,
  next: SelfServiceSteps.SIGNATURE,
  previous: SelfServiceSteps.PARKING,
  data:{
    isVisible: ({ status }, { displayBasket }) => isNotAnswered(status) && displayBasket,
  }
};

export const KIOSK_USAGE: SelfServiceStep = {
  ...COMMON_KIOSK_USAGE,
  previous: SelfServiceSteps.QUESTIONS,
  next: SelfServiceSteps.PARKING,
  data: {
    isVisible: ({
      type, origin, status, breakdownServiceAuthorType,
    }, config) => {
      const isCheckOutVisible = isKioskPreferenceQuestionVisible(
        KioskPreferencesQuestionsIds.CHECK_OUT,
        type,
        origin,
        config,
      );

      return isNotAnswered(status)
          && isCheckOutVisible
          && breakdownServiceAuthorType === BreakdownServiceAuthorType.CUSTOMER;
    },
  },
};

export const PARKING: SelfServiceStep = {
  ...COMMON_PARKING,
  previous: SelfServiceSteps.KIOSK_USAGE,
  next: SelfServiceSteps.BASKET,
};

type OnSiteBreakdownSteps =
  SelfServiceSteps.CUSTOMER_INFO
  | SelfServiceSteps.BREAKDOWN_INFO
  | SelfServiceSteps.EXTRAS
  | SelfServiceSteps.QUESTIONS
  | SelfServiceSteps.BASKET
  | SelfServiceSteps.KIOSK_USAGE
  | SelfServiceSteps.PARKING
  | SelfServiceSteps.SIGNATURE
  | SelfServiceSteps.FINAL_INSTRUCTIONS;

const ON_SITE_BREAKDOWN_STEPS: Flow<OnSiteBreakdownSteps> = {
  CUSTOMER_INFO,
  BREAKDOWN_INFO,
  EXTRAS,
  QUESTIONS,
  BASKET,
  KIOSK_USAGE,
  PARKING,
  SIGNATURE: {
    ...BREAKDOWN_SIGNATURE,
    previous: BASKET.key,
    next: FINAL_INSTRUCTIONS.key,
    data: { ...BREAKDOWN_SIGNATURE.data, isFinalizeStep: true },
  },
  FINAL_INSTRUCTIONS: { ...FINAL_INSTRUCTIONS, previous: BREAKDOWN_SIGNATURE.key },
};

export default ON_SITE_BREAKDOWN_STEPS;
